import { useState, useEffect } from 'react';
import type { IrsFormData } from '@spi/shared';
import { usePrefillIrsForm, useGenerateIrsForm, useSavedSignatures, useSaveSignature, useDeleteSignature } from '../../../api/irs';
import { SignatureCanvas } from './SignatureCanvas';
import toast from 'react-hot-toast';

interface IrsFormModalProps {
  donationId: number;
  /** Pre-existing form data when regenerating */
  existingFormData?: IrsFormData | null;
  onClose: () => void;
  onGenerated: () => void;
}

export function IrsFormModal({ donationId, existingFormData, onClose, onGenerated }: IrsFormModalProps) {
  const [doneeMode, setDoneeMode] = useState<'spi' | 'recipient'>(existingFormData?.doneeMode || 'recipient');
  const { data: prefillData, isLoading: prefillLoading } = usePrefillIrsForm(donationId, doneeMode, !existingFormData);
  const generateMutation = useGenerateIrsForm();
  const { data: savedSignatures } = useSavedSignatures(true);
  const saveSignatureMutation = useSaveSignature();
  const deleteSignatureMutation = useDeleteSignature();

  // Form state
  const [formData, setFormData] = useState<IrsFormData | null>(existingFormData || null);

  // Signature state
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [selectedSavedSigId, setSelectedSavedSigId] = useState<number | null>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'saved'>('draw');
  const [saveNewSignature, setSaveNewSignature] = useState(false);
  const [newSignatureLabel, setNewSignatureLabel] = useState('');

  // Auto-select first saved signature when available
  useEffect(() => {
    if (savedSignatures && savedSignatures.length > 0 && !selectedSavedSigId) {
      setSignatureMode('saved');
      setSelectedSavedSigId(savedSignatures[0].id);
    }
  }, [savedSignatures, selectedSavedSigId]);

  // Initialize form data from prefill or existing
  useEffect(() => {
    if (!existingFormData && prefillData) {
      setFormData(prefillData);
    }
  }, [prefillData, existingFormData]);

  // When donee mode changes and we're doing a fresh prefill (not regenerating), re-fetch will auto-update
  useEffect(() => {
    if (!existingFormData && prefillData) {
      setFormData(prefillData);
    }
  }, [doneeMode, prefillData, existingFormData]);

  const updateField = <K extends keyof IrsFormData>(key: K, value: IrsFormData[K]) => {
    setFormData((prev) => prev ? { ...prev, [key]: value } : prev);
  };

  const getActiveSignature = (): string | undefined => {
    if (signatureMode === 'saved' && selectedSavedSigId) {
      return savedSignatures?.find((s) => s.id === selectedSavedSigId)?.signatureData;
    }
    return signatureDataUrl || undefined;
  };

  const handleGenerate = async () => {
    if (!formData) return;
    const sig = getActiveSignature();
    try {
      await generateMutation.mutateAsync({
        donationId,
        formData: { ...formData, doneeMode },
        signatureDataUrl: sig,
      });
      // Optionally save new signature
      if (signatureMode === 'draw' && saveNewSignature && signatureDataUrl) {
        await saveSignatureMutation.mutateAsync({
          label: newSignatureLabel || 'My Signature',
          signatureData: signatureDataUrl,
        });
      }
      toast.success(existingFormData ? 'IRS form regenerated' : 'IRS form generated');
      onGenerated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate');
    }
  };

  const showSectionB = (formData?.fairMarketValue ?? 0) > 5000;

  if (!existingFormData && prefillLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent mx-auto" />
          <p className="mt-4 text-sm text-spi-slate">Loading form data...</p>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">{existingFormData ? 'Regenerate' : 'Generate'} IRS Form 8283</h3>
            <p className="text-sm text-spi-slate">Review and edit all fields before generating the PDF.</p>
          </div>
          <button onClick={onClose} className="text-spi-slate hover:text-spi-charcoal">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Donee Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          <label className="text-sm font-semibold text-spi-charcoal block mb-2">Donee Organization</label>
          <div className="flex gap-3">
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${doneeMode === 'spi' ? 'bg-spi-green text-white border-spi-green' : 'border-gray-300 hover:bg-gray-100'}`}>
              <input type="radio" name="doneeMode" value="spi" checked={doneeMode === 'spi'} onChange={() => setDoneeMode('spi')} className="sr-only" />
              <span className="text-sm font-medium">SPI / Platform</span>
            </label>
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${doneeMode === 'recipient' ? 'bg-spi-green text-white border-spi-green' : 'border-gray-300 hover:bg-gray-100'}`}>
              <input type="radio" name="doneeMode" value="recipient" checked={doneeMode === 'recipient'} onChange={() => setDoneeMode('recipient')} className="sr-only" />
              <span className="text-sm font-medium">Recipient Organization</span>
            </label>
          </div>
        </div>

        {/* Identification */}
        <fieldset className="mb-6">
          <legend className="text-sm font-semibold text-spi-charcoal mb-3">Identification</legend>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Taxpayer Name</label>
              <input className="input" value={formData.taxpayerName || ''} onChange={(e) => updateField('taxpayerName', e.target.value)} />
            </div>
            <div>
              <label className="input-label">Identifying Number (SSN/EIN)</label>
              <input className="input" value={formData.identifyingNumber || ''} onChange={(e) => updateField('identifyingNumber', e.target.value)} />
            </div>
          </div>
        </fieldset>

        {/* Section A */}
        <fieldset className="mb-6">
          <legend className="text-sm font-semibold text-spi-charcoal mb-3">Section A: Donated Property</legend>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">(a) Donee Organization Name</label>
              <input className="input" value={formData.doneeName || ''} onChange={(e) => updateField('doneeName', e.target.value)} />
            </div>
            <div>
              <label className="input-label">(a) Donee Address</label>
              <input className="input" value={formData.doneeAddress || ''} onChange={(e) => updateField('doneeAddress', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="input-label">(c) Description of Property</label>
              <input className="input" value={formData.description} onChange={(e) => updateField('description', e.target.value)} />
            </div>
            <div>
              <label className="input-label">(c) Condition</label>
              <select className="input" value={formData.propertyCondition || 'New/Unopened'} onChange={(e) => updateField('propertyCondition', e.target.value)}>
                <option>New/Unopened</option>
                <option>Good</option>
                <option>Fair</option>
                <option>Poor</option>
              </select>
            </div>
            <div>
              <label className="input-label">(d) Date of Contribution</label>
              <input type="date" className="input" value={formData.dateOfContribution} onChange={(e) => updateField('dateOfContribution', e.target.value)} />
            </div>
            <div>
              <label className="input-label">(e) Date Acquired by Donor</label>
              <input type="date" className="input" value={formData.dateAcquiredByDonor || ''} onChange={(e) => updateField('dateAcquiredByDonor', e.target.value)} />
            </div>
            <div>
              <label className="input-label">(f) How Acquired</label>
              <select className="input" value={formData.howAcquired || 'Purchased'} onChange={(e) => updateField('howAcquired', e.target.value)}>
                <option>Purchased</option>
                <option>Produced</option>
                <option>Donated</option>
                <option>Inherited</option>
                <option>Exchange</option>
              </select>
            </div>
            <div>
              <label className="input-label">(g) Donor's Cost or Adjusted Basis</label>
              <input type="number" step="0.01" className="input" value={formData.donorCostOrBasis ?? ''} onChange={(e) => updateField('donorCostOrBasis', e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
            <div>
              <label className="input-label">(h) Fair Market Value</label>
              <input type="number" step="0.01" className="input" value={formData.fairMarketValue} onChange={(e) => updateField('fairMarketValue', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className="input-label">(i) Method Used to Determine FMV</label>
              <input className="input" value={formData.methodOfValuation} onChange={(e) => updateField('methodOfValuation', e.target.value)} />
            </div>
          </div>
        </fieldset>

        {/* Section B (shown only if FMV > $5,000) */}
        {showSectionB && (
          <>
            <fieldset className="mb-6">
              <legend className="text-sm font-semibold text-spi-charcoal mb-3">Section B: Donated Property Over $5,000</legend>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Property Type</label>
                  <input className="input" value={formData.propertyType || ''} onChange={(e) => updateField('propertyType', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">Physical Condition</label>
                  <input className="input" value={formData.physicalCondition || ''} onChange={(e) => updateField('physicalCondition', e.target.value)} />
                </div>
              </div>
            </fieldset>

            <fieldset className="mb-6">
              <legend className="text-sm font-semibold text-spi-charcoal mb-3">Part IV: Appraiser Information</legend>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Appraiser Name</label>
                  <input className="input" value={formData.appraiserName || ''} onChange={(e) => updateField('appraiserName', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">Title / Designation</label>
                  <input className="input" value={formData.appraiserTitle || ''} onChange={(e) => updateField('appraiserTitle', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">Appraiser Address</label>
                  <input className="input" value={formData.appraiserAddress || ''} onChange={(e) => updateField('appraiserAddress', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">Appraiser ID Number</label>
                  <input className="input" value={formData.appraiserIdNumber || ''} onChange={(e) => updateField('appraiserIdNumber', e.target.value)} />
                </div>
              </div>
            </fieldset>

            <fieldset className="mb-6">
              <legend className="text-sm font-semibold text-spi-charcoal mb-3">Part V: Donee Acknowledgment</legend>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Organization Name</label>
                  <input className="input" value={formData.doneeAckOrgName || ''} onChange={(e) => updateField('doneeAckOrgName', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">EIN</label>
                  <input className="input" value={formData.doneeAckEin || ''} onChange={(e) => updateField('doneeAckEin', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="input-label">Address</label>
                  <input className="input" value={formData.doneeAckAddress || ''} onChange={(e) => updateField('doneeAckAddress', e.target.value)} />
                </div>
                <div>
                  <label className="input-label">Date Received</label>
                  <input type="date" className="input" value={formData.doneeAckDateReceived || ''} onChange={(e) => updateField('doneeAckDateReceived', e.target.value)} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="unrelatedUse"
                    checked={formData.doneeUnrelatedUse || false}
                    onChange={(e) => updateField('doneeUnrelatedUse', e.target.checked)}
                    className="rounded border-gray-300 text-spi-green focus:ring-spi-green"
                  />
                  <label htmlFor="unrelatedUse" className="text-sm text-spi-slate">Intends to use for unrelated purpose</label>
                </div>
              </div>
            </fieldset>
          </>
        )}

        {/* Signature — only applicable when SPI is the donee */}
        <fieldset className="mb-6">
          <legend className="text-sm font-semibold text-spi-charcoal mb-3">Authorized Signature</legend>
          {doneeMode === 'recipient' ? (
            <p className="text-xs text-spi-slate mb-3">
              The recipient organization is the donee — the Part V signature line will be left blank for them to sign.
            </p>
          ) : (
            <p className="text-xs text-spi-slate mb-3">
              SPI is the donee — this signature will be placed on the Part V Donee Acknowledgment line of the PDF.
            </p>
          )}

          {savedSignatures && savedSignatures.length > 0 && (
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => { setSignatureMode('saved'); setSignatureDataUrl(null); }}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${signatureMode === 'saved' ? 'bg-spi-green text-white border-spi-green' : 'border-gray-300 text-spi-slate hover:bg-gray-50'}`}
              >
                Use Saved Signature
              </button>
              <button
                type="button"
                onClick={() => { setSignatureMode('draw'); setSelectedSavedSigId(null); }}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${signatureMode === 'draw' ? 'bg-spi-green text-white border-spi-green' : 'border-gray-300 text-spi-slate hover:bg-gray-50'}`}
              >
                Draw New
              </button>
            </div>
          )}

          {signatureMode === 'saved' && savedSignatures && savedSignatures.length > 0 ? (
            <div className="space-y-3">
              {savedSignatures.map((sig) => (
                <div
                  key={sig.id}
                  onClick={() => setSelectedSavedSigId(sig.id)}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedSavedSigId === sig.id ? 'border-spi-green bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <img src={sig.signatureData} alt={sig.label} className="h-10 bg-white rounded border border-gray-100" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{sig.label}</p>
                    <p className="text-xs text-spi-slate">{new Date(sig.createdAt).toLocaleDateString()}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedSavedSigId === sig.id) setSelectedSavedSigId(null);
                      deleteSignatureMutation.mutate(sig.id);
                    }}
                    className="text-xs text-red-400 hover:text-red-600"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <SignatureCanvas onSignature={setSignatureDataUrl} />
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="save-sig-modal"
                  checked={saveNewSignature}
                  onChange={(e) => setSaveNewSignature(e.target.checked)}
                  className="rounded border-gray-300 text-spi-green focus:ring-spi-green"
                />
                <label htmlFor="save-sig-modal" className="text-sm text-spi-slate">Save this signature for future use</label>
              </div>
              {saveNewSignature && (
                <input
                  value={newSignatureLabel}
                  onChange={(e) => setNewSignatureLabel(e.target.value)}
                  placeholder="Signature label (e.g., Brian Love)"
                  className="input mt-2 text-sm"
                />
              )}
            </div>
          )}
        </fieldset>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button onClick={onClose} className="btn btn-outline flex-1">Cancel</button>
          <button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="btn btn-primary flex-1"
          >
            {generateMutation.isPending ? 'Generating...' : existingFormData ? 'Regenerate & Sign' : 'Generate & Sign'}
          </button>
        </div>
      </div>
    </div>
  );
}
